import React, { useState, useEffect, useRef } from 'react';
import 'ol/ol.css';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/PriorityHigh';
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer';
import { Style, Text, Fill, Stroke } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import { toFormatedDate } from 'utils/commonFunc';
// import { fromLonLat, get as getProjection } from 'ol/proj';
import SuwonMapCore from 'map/SuwonMapCore';
import Avatar from '@mui/material/Avatar';
import { getVectorContext } from 'ol/render';
import { easeOut } from 'ol/easing';
import { unByKey } from 'ol/Observable';
import { Circle as CircleStyle } from 'ol/style';

const ItsWebMap = React.forwardRef((props, ref) => {
    const { lightMode = false } = props;

    const divRef = useRef(null);
    const [mapObj, setMapObj] = useState({});

    React.useImperativeHandle(ref, () => ({
        AddLink2GeoJson,
        SetViewCenter,
        DeSelectAll,
        DefaultViewZoom,
        SetMapLayerVisibility,
        ShowTooltip,
        SetAccessRoadText,
    }))

    useEffect(() => {
        if (divRef.current) {
            const map = new SuwonMapCore(divRef.current, { lightMode });

            const acsrTextSource = new VectorSource();
            const acsrTextLayer = new VectorLayer({ source: acsrTextSource });
            map.addLayer(acsrTextLayer);

            setMapObj({ map, acsrTextLayer });
            return () => map.setTarget(undefined);
        }
    }, [divRef, lightMode]);

    const AddLink2GeoJson = (link2Data, intersectionData, changeSelectAction, routeFindErrorAction, isSingleMode = false, currentViewType = 'avgSpeed') => {
        const map = mapObj.map;
        if (map && !mapObj.link2Layer) {
            map.setTooltipLayer({ lightMode });
            const link2Layer = map.addSelectLink2GeoJson(link2Data);
            const intersectionLayer = map.addIntersectionGeoJson(intersectionData);

            link2Layer.setVisible(currentViewType === 'avgSpeed');
            intersectionLayer.setVisible(true);

            intersectionLayer.setTooltipMode(true);
            if (!isSingleMode) {
                intersectionLayer.setLink2SelectedMode(true, link2Layer);
                link2Layer.setSelectedMode(true);
            } else {
                link2Layer.setSingleSelectedMode(true);
            }

            link2Layer.setTooltipMode(true);
            link2Layer.setOnChangeSelectBind(changeSelectAction);
            link2Layer.setOnRouteFindErrorBind(routeFindErrorAction);

            if (intersectionLayer.setOnChangeSelectBind) {
                intersectionLayer.setOnChangeSelectBind((result) => {
                    if (result && result.count === 0) {
                        if (mapObj.acsrTextLayer) {
                            mapObj.acsrTextLayer.getSource().clear();
                        }
                    }
                    changeSelectAction(result);
                });
            }

            setMapObj(prevMapObj => ({ ...prevMapObj, link2Layer, intersectionLayer }));
        }
    }

    // 애니메이션 함수 (파란 원 퍼짐 효과)
    const flash = (feature) => {
        const layer = mapObj.acsrTextLayer; 
        const map = mapObj.map;
        
        if (!map || !feature || !layer) return;

        const duration = 3000;
        const start = Date.now();
        const flashGeom = feature.getGeometry().clone();
        
        const listenerKey = layer.on('postrender', (event) => {
            const frameState = event.frameState;
            const elapsed = frameState.time - start;
            if (elapsed >= duration) {
                unByKey(listenerKey);
                return;
            }
            
            const vectorContext = getVectorContext(event);
            const elapsedRatio = elapsed / duration;
            const radius = easeOut(elapsedRatio) * 50 + 5;
            const opacity = easeOut(1 - elapsedRatio);

            const style = new Style({
                image: new CircleStyle({
                    radius: radius,
                    fill: new Fill({ color: `rgba(100, 149, 237, ${opacity * 0.6})` }),
                    stroke: new Stroke({ color: `rgba(100, 149, 237, ${opacity})`, width: 2 + opacity })
                })
            });
            
            vectorContext.setStyle(style);
            vectorContext.drawGeometry(flashGeom);
            
            map.render();
        });
    };

    const SetViewCenter = (wgsX, wgsY, id, zoom=15) => {
        setMapObj(mapObj => {
            const view = mapObj.map?.getView();
            if (view) {
                view.setZoom(zoom);
                view.setCenter(fromLonLat([wgsX, wgsY]));
                
                const linkLayer = mapObj?.link2Layer;
                if(linkLayer && id) {
                    linkLayer.getSource()?.forEachFeature(feature => {
                        if (feature.get('LINK2_ID') === id) {
                            feature.set('selected', true);
                            // 기존에 정의된 링크용 flash가 있으면 실행
                            if (typeof linkLayer.flash === 'function') linkLayer.flash(mapObj.map, feature);
                        } else {
                            feature.unset('selected');
                        }
                    });
                }

                const intsLayer = mapObj?.intersectionLayer;
                if (intsLayer && id) {
                    let foundFeature = null;
                    intsLayer.getSource()?.forEachFeature(feature => {
                        if (feature.get('crsrdId') === id) {
                            foundFeature = feature;
                        }
                    });
                    
                    if (foundFeature) {
                        flash(foundFeature);
                    }
                }
            }
            return mapObj;
        })
    }

    const DeSelectAll = () => {
        setMapObj(mapObj => {
            mapObj?.link2Layer?.setDeSelectAll();
            if (mapObj?.acsrTextLayer) {
                mapObj.acsrTextLayer.getSource().clear();
            }
            return mapObj;
        })
    }

    const DefaultViewZoom = () => {
        setMapObj(mapObj => {
            mapObj?.map?.setDefaultViewZoom();
            return mapObj;
        })
    }

    const SetMapLayerVisibility = (viewType) => {
        const linkLayer = mapObj.link2Layer;
        if (linkLayer) {
            if (typeof linkLayer.setVisible === 'function') {
                linkLayer.setVisible(viewType === 'avgSpeed');
            } else if (linkLayer.layer && typeof linkLayer.layer.setVisible === 'function') {
                linkLayer.layer.setVisible(viewType === 'avgSpeed');
            }
        }
    };

    const ShowTooltip = (id, wgsX, wgsY) => {
        const map = mapObj.map;
        if (!map || !map.tooltipLayer) return;
        const coordinate = fromLonLat([wgsX, wgsY]);

        let foundFeature = null;
        let tooltipText = "";

        if (mapObj.link2Layer) {
            const source = mapObj.link2Layer.getSource();
            source.forEachFeature(f => {
                if (f.get('LINK2_ID') === id) foundFeature = f;
            });

            if (foundFeature) {
                const name = foundFeature.get('name');
                if (name) {
                    const fname = foundFeature.get('fname');
                    const tname = foundFeature.get('tname');
                    const speed = parseFloat(foundFeature.get('speed')) || 0;
                    const yyyy = foundFeature.get('yyyy') || '';
                    const mm = foundFeature.get('mm') || '';
                    const dd = foundFeature.get('dd') || '';
                    const hh = foundFeature.get('hh') || '';
                    
                    tooltipText = `도로명 : ${name}\n(${fname}-${tname})\n평균속도 : ${speed} km/h\n${yyyy}년 ${mm}월 ${dd}일 ${hh}시 기준`;
                } else {
                    tooltipText = `정보없음\n(${id})`;
                }
            }
        }
        if (foundFeature) {
            map.currentTooltipType = 'SELECT';
            map.tooltipLayer.setTooltip(coordinate, tooltipText, foundFeature);
        }
    }

    const getAngleDifference = (angle1, angle2) => {
        let diff = Math.abs(angle1 - angle2);
        diff = diff % (2 * Math.PI);
        if (diff > Math.PI) diff = 2 * Math.PI - diff;
        if (diff > Math.PI / 2) diff = Math.PI - diff;
        return diff;
    };

    const SetAccessRoadText = (acsrList, center, crsrdNm = "") => {
        // 하드코딩 각도 설정 (0도: 수평, 시계방향 회전, 북or북동부터 시작해서 서or북서까지 시계방향으로 매핑)
        const EXCEPTION_CRSRD_ANGLES = {
            "장안구청사거리": [305, 37, 305, 37], 
            "관터사거리": [278, 20, 284, 15],
            "망포역사거리": [284, 355, 307, 358],
            "태장초교사거리": [260, 355, 260, 355],
            "권선지하차도사거리": [282, 15, 280, 15], 
            "터미널사거리": [292, 21, 292, 34],
            "종로삼거리": [267, 358, 267, 358],
            "지동사거리": [270, 340, 270, 340],
            "교동사거리": [277, 335, 240, 335],
            "세류사거리": [270, 34, 270, 34],
            "솔대사거리": [302, 37, 302, 32], 
            "도청오거리": [285, 330, 248, 330, 248],
            "고양삼거리": [55, 335, 62, 335],
            "화서사거리": [292, 0, 282, 0],
            "수원역광장(남측)삼거리": [63, 335, 63],
            "벌말교차로": [35, 307, 35, 307], 
            "벌터교차로": [65, 335, 60, 335],
            "평동사거리": [263, 340, 263, 340],
            "성대역사거리": [273, 5, 290, 5],
            "구운사거리": [271, 37, 271, 60],
            "구운오거리": [35, 341, 35, 341, 305],
            "신동사거리": [287, 355, 280, 355], 
            "삼성삼거리": [12, 328, 12],
            "권선구청삼거리": [264, 350, 260, 350],
            "오목천교사거리": [77, 340, 71, 340],
            "농진청사거리": [45, 347, 45, 353],
            "버들삼거리": [55, 320, 55],
            "효자문사거리": [295, 61, 300, 58],
            "만석공원사거리": [60, 53, 60, 322],
            "송림초교사거리": [26, 333, 55, 330],
            "호매실홈플러스사거리": [70, 338, 68, 338],
            "하고렴사거리": [283, 20, 288, 20],
            "도청로삼거리": [312, 48, 312],
            "중소기업센터삼거리": [315, 44, 315, 44],
            "호매실지하차도사거리": [273, 3, 273, 3],
            "호반마을삼거리": [283, 2, 285],
            "호매실사거리": [70, 4, 74, 4],
            "센트럴파크로사거리": [310, 42, 310, 37],
            "컨벤션센터사거리": [311, 45, 311, 45],
            "지지대사거리": [266, 266, 350],
        };

        const EXCEPTION_CRSRD_NAMES = [
            "장안구청사거리", "관터사거리", "망포역사거리", "태장초교사거리", "권선지하차도사거리", 
            "터미널사거리", "종로삼거리", "지동사거리", "교동사거리", "세류사거리", "솔대사거리", 
            "도청오거리", "고양삼거리", "화서사거리", "수원역광장(남측)삼거리", "벌말교차로", 
            "벌터교차로", "평동사거리", "성대역사거리", "구운사거리", "구운오거리", "신동사거리", 
            "삼성삼거리", "권선구청삼거리", "오목천교사거리", "농진청사거리", "버들삼거리", 
            "효자문사거리", "만석공원사거리", "송림초교사거리", "호매실홈플러스사거리", "하고렴사거리", "도청로삼거리", "중소기업센터삼거리",
            "호매실지하차도사거리", "호반마을삼거리", "호매실사거리", "센트럴파크로사거리", "컨벤션센터사거리", "지지대사거리"
        ];

        const EXCEPTION_CRSRD_POS_ADJUST = {
            "수원역광장(남측)삼거리": [
                { x: -0.0002, y: 0.0002 },
                { x: 0.0007, y: -0.0004 },
                { x: -0.0003, y: 0.0002 }
            ]
        };
        
        const layer = mapObj.acsrTextLayer;
        const linkLayer = mapObj.link2Layer;

        if (!layer) return;

        const source = layer.getSource();
        source.clear();

        if (!acsrList || acsrList.length === 0 || !center) return;

        const uniqueAcsrList = [];
        const seenNames = new Set();
        acsrList.forEach(item => {
            if (!seenNames.has(item.acsrNm)) {
                seenNames.add(item.acsrNm);
                uniqueAcsrList.push(item);
            }
        });

        const isExceptionCrsrd = EXCEPTION_CRSRD_NAMES.includes(crsrdNm);
        const manualAngles = EXCEPTION_CRSRD_ANGLES[crsrdNm];
        const manualPosAdjust = EXCEPTION_CRSRD_POS_ADJUST[crsrdNm];
        const centerProj = fromLonLat([parseFloat(center.lng), parseFloat(center.lat)]);
        const linkSource = linkLayer ? linkLayer.getSource() : null;

        uniqueAcsrList.forEach((item, index) => {
            if (item.lng && item.lat && item.acsrNm) {
                let targetLng = parseFloat(item.lng);
                let targetLat = parseFloat(item.lat);

                if (manualPosAdjust && manualPosAdjust[index]) {
                    targetLng += (manualPosAdjust[index].x || 0);
                    targetLat += (manualPosAdjust[index].y || 0);
                }

                const targetProj = fromLonLat([targetLng, targetLat]);

                const dx = targetProj[0] - centerProj[0];
                const dy = targetProj[1] - centerProj[1];

                const distance = Math.sqrt(dx * dx + dy * dy);

                // 텍스트를 교차로부터 이동시길 가중치 설정 (숫자가 클수록 더 멀어짐)
                const offsetFactor = 40; 

                let finalTargetProj = targetProj;
                if (distance > 0) {
                    finalTargetProj = [
                        targetProj[0] + (dx / distance) * offsetFactor,
                        targetProj[1] + (dy / distance) * offsetFactor
                    ];
                }

                const feature = new Feature({ geometry: new Point(finalTargetProj) });
                
                let baseRotation = -Math.atan2(dy, dx); 
                let finalRotation = baseRotation; 
                
                // 하드코딩 여부 체크 플래그
                let isManualSet = false;
                if (manualAngles && manualAngles[index] !== undefined) {
                    finalRotation = manualAngles[index] * (Math.PI / 180);
                    isManualSet = true;
                } 
                else if (isExceptionCrsrd) {
                    finalRotation = baseRotation;
                }
                else if (linkSource) {
                    const bufferSize = 30; 
                    const extent = [
                        targetProj[0] - bufferSize, targetProj[1] - bufferSize,
                        targetProj[0] + bufferSize, targetProj[1] + bufferSize
                    ];
                    const neighborFeatures = linkSource.getFeaturesInExtent(extent);
                    
                    let bestGlobalSegment = null;
                    let minAngleDiff = Number.MAX_VALUE;
                    const MIN_LEN_SQ = 100;
                    const STRICT_ANGLE_LIMIT = Math.PI / 6;
                    
                    neighborFeatures.forEach(polyFeature => {
                        const geom = polyFeature.getGeometry();
                        const coords = geom.getType() === 'Polygon' ? geom.getCoordinates()[0] : geom.getCoordinates();

                        for (let i = 0; i < coords.length - 1; i++) {
                            const p1 = coords[i];
                            const p2 = coords[i + 1];
                            const sdx = p2[0] - p1[0];
                            const sdy = p2[1] - p1[1];
                            const lenSq = sdx * sdx + sdy * sdy;
                            if (lenSq < MIN_LEN_SQ) continue;
                            const edgeAngle = -Math.atan2(sdy, sdx);
                            const diff = getAngleDifference(baseRotation, edgeAngle);
                            if (diff > STRICT_ANGLE_LIMIT) continue;
                            if (diff < minAngleDiff) {
                                minAngleDiff = diff;
                                bestGlobalSegment = { angle: edgeAngle, lenSq: lenSq };
                            }
                        }
                    });

                    if (bestGlobalSegment) {
                        finalRotation = bestGlobalSegment.angle;
                    }
                }

                if (!isManualSet) {
                    let diff = Math.abs(baseRotation - finalRotation);
                    while(diff > Math.PI) diff -= 2 * Math.PI;
                    diff = Math.abs(diff);
                    if (diff > Math.PI / 2) {
                        finalRotation += Math.PI;
                    }

                    const rotateCheck = (finalRotation % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
                    if (rotateCheck > Math.PI / 2 && rotateCheck < 3 * Math.PI / 2) {
                        finalRotation += Math.PI;
                    }
                }

                feature.setStyle((feature, resolution) => {
                    if (resolution > 3) return null;
                    return new Style({
                        text: new Text({
                            text: item.acsrNm,
                            font: 'bold 14px "Malgun Gothic", sans-serif',
                            fill: new Fill({ color: '#fff' }),
                            stroke: new Stroke({ color: '#000', width: 2 }),
                            overflow: true,
                            rotation: finalRotation,
                            offsetY: 0 
                        })
                    });
                });

                source.addFeature(feature);
            }
        });
    };


    return (
        <React.Fragment>
            <div ref={divRef} alt={mapObj?.test} />
        </React.Fragment>
    );
});

export default ItsWebMap;
